import { MoreHorizontal } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { createSelectColumn, SortableHeader } from '~/components/ui/data-table';

interface Guardian {
  person: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface FamilyRow {
  id: string;
  name: string;
  slug: string;
  studentCount: number;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  guardians: Guardian[];
}

interface CreateFamilyColumnsOptions {
  onViewFamily: (family: FamilyRow) => void;
  onDeleteFamily: (family: FamilyRow) => void;
  hasEnrollments?: (family: FamilyRow) => boolean;
}

/**
 * Format guardians for display
 * Shows first guardian name with "+N more" if multiple
 */
function formatGuardians(guardians: Guardian[] | null | undefined) {
  if (!guardians || guardians.length === 0) {
    return '—';
  }

  const firstGuardian = guardians[0].person;
  const displayName = `${firstGuardian.firstName} ${firstGuardian.lastName}`;

  if (guardians.length === 1) {
    return displayName;
  }

  return `${displayName} +${guardians.length - 1} more`;
}

export function createFamilyColumns({ onViewFamily, onDeleteFamily, hasEnrollments }: CreateFamilyColumnsOptions) {
  return [
    // Selection column
    createSelectColumn(),

    // Family Name
    {
      accessorKey: 'name',
      header: ({ column }: any) => (
        <SortableHeader column={column}>Family Name</SortableHeader>
      ),
      cell: ({ row }: any) => {
        const name = row.getValue('name');
        return <span className="font-medium">{name}</span>;
      },
    },

    // Guardians
    {
      accessorKey: 'guardians',
      header: 'Guardians',
      cell: ({ row }: any) => {
        const guardians = row.getValue('guardians');
        return formatGuardians(guardians);
      },
      enableSorting: false,
    },

    // Student Count
    {
      accessorKey: 'studentCount',
      header: ({ column }: any) => (
        <SortableHeader column={column}>Students</SortableHeader>
      ),
      cell: ({ row }: any) => {
        const count = row.getValue('studentCount');
        return <span className="tabular-nums">{count}</span>;
      },
      meta: {
        facet: {
          title: 'Students',
          getKey: (family: FamilyRow) => family.studentCount > 0 ? 'WITH_STUDENTS' : 'NO_STUDENTS',
          getLabel: (family: FamilyRow) => family.studentCount > 0 ? 'With students' : 'No students',
        },
      },
    },

    // Email
    {
      accessorKey: 'primaryContactEmail',
      header: ({ column }: any) => (
        <SortableHeader column={column}>Email</SortableHeader>
      ),
      cell: ({ row }: any) => {
        const email = row.getValue('primaryContactEmail');
        return email || '—';
      },
      meta: {
        facet: {
          title: 'Email',
          getKey: (family: FamilyRow) => family.primaryContactEmail ? 'HAS_EMAIL' : 'NO_EMAIL',
          getLabel: (family: FamilyRow) => family.primaryContactEmail ? 'Email on file' : 'No email',
        },
      },
    },

    // Phone
    {
      accessorKey: 'primaryContactPhone',
      header: 'Phone',
      cell: ({ row }: any) => {
        const phone = row.getValue('primaryContactPhone');
        return phone || '—';
      },
      enableSorting: false,
    },

    // Actions
    {
      id: 'actions',
      cell: ({ row }: any) => {
        const family = row.original as FamilyRow;

        return (
          <div data-no-row-click>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewFamily(family);
                  }}
                  onSelect={(e) => {
                    e.preventDefault();
                    onViewFamily(family);
                  }}
                >
                  View family
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigator.clipboard.writeText(family.primaryContactEmail || '')}
                  disabled={!family.primaryContactEmail}
                >
                  Copy email
                </DropdownMenuItem>
                {onDeleteFamily && !hasEnrollments?.(family) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteFamily(family);
                      }}
                      onSelect={(e) => {
                        e.preventDefault();
                        onDeleteFamily(family);
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      Delete family
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
